import React, { Fragment, useEffect, useState } from "react";
import Input from "../../../form/Input";
import Select from "../../../form/Select";
import useForm from "../../../../hooks/useForm";
import Button from "../../../form/Button";
import useMutation from "../../../../hooks/useMutation";
import Radio from "../../../form/Radio";
import Switch from "../../../form/Switch";
import { userAddEdit } from "../../../../services/userService";
import { generatePassword } from "../../../../util/Flag";

const validation = {
  firstName: { required: true, message: "First name is required" },
  lastName: { required: true, message: "Last name is required" },
  email: { required: true, message: "Email is required" },
  password: { required: true, message: "password is required" },
  confirmPassword: { required: true, message: "Confirm password is required" },
  roleCode: { required: true, message: "Role is required" },
};

const ManageUser = ({ getResponse = () => {}, close = () => {}, data }) => {
  const [selectedOption, setSelectedOption] = useState(
    data?._id ? "manualPassword" : "autoPassword"
  );
  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    resetField,
    formValidate,
  } = useForm({
    validationRules: validation,
    initialState: {
      isActive: true,
      forcePassword: true,
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
      roleCode: "",
    },
  });

  useEffect(() => {
    if (data?._id) {
      delete validation?.confirmPassword;
      delete validation?.password;
      setFieldsValue(data);
    }
  }, [data]);

  useEffect(() => {
    if (selectedOption === "autoPassword") {
      const tempPassword = generatePassword(10); // or 8+
      setFieldsValue({
        password: tempPassword,
        confirmPassword: tempPassword,
      });
    } else {
      setFieldsValue({
        password: "",
        confirmPassword: "",
      });
    }
  }, [selectedOption]);

  const {
    mutate,
    error,
    isPending: isLoading,
  } = useMutation({
    mutationFn: async (val) => {
      const {
        password,
        confirmPassword,
        isActive,
        forcePassword,
        welcomeEmail,
        ...rest
      } = val;
      return await userAddEdit(
        data?._id
          ? { ...(data?._id && { id: data?._id, ...rest }) }
          : {
              ...val,
            }
      );
    },
    onSuccess: (res) => {
      if (res.success) {
        setSelectedOption(data?._id ? "manualPassword" : "autoPassword");
        resetField();
      }
      getResponse(res);
      return res;
    },
    onError: (error) => {
      console.log(error);
    },
  });

  return (
    <form onSubmit={(event) => handleSubmit(event, mutate)}>
      <div className="grid grid-cols-4 gap-x-4 gap-y-4">
        <div className="relative col-span-2">
          <Input
            label="First Name"
            inputPlaceholder={"Enter  First Name"}
            inputName="firstName"
            value={values?.firstName}
            onChange={handleChange}
            {...formValidate(errors, "firstName")}
          />
        </div>
        <div className="relative col-span-2">
          <Input
            label="Last Name"
            inputPlaceholder={"Enter Last Name"}
            inputName="lastName"
            value={values?.lastName}
            onChange={handleChange}
            {...formValidate(errors, "lastName")}
          />
        </div>
        <div className="relative col-span-2">
          <Input
            label="Email"
            inputPlaceholder={"Enter Email"}
            inputName="email"
            inputType={"email"}
            value={values?.email}
            isDisabled={data?._id}
            onChange={handleChange}
            {...formValidate(errors, "email")}
          />
        </div>
        <div className="relative col-span-2">
          <Input
            label="Phone"
            inputPlaceholder={"Enter phone"}
            inputName="phone"
            value={values?.phone}
            onChange={handleChange}
            {...formValidate(errors, "phone")}
          />
        </div>
        {!data?._id && (
          <Fragment>
            <div className="relative col-span-2">
              <Input
                label="Password"
                inputPlaceholder={"Enter Password"}
                inputName="password"
                inputType={"password"}
                value={values?.password}
                onChange={handleChange}
                {...formValidate(errors, "password")}
              />
            </div>
            <div className="relative col-span-2">
              <Input
                label="Confirm Password"
                inputPlaceholder={"Enter Confirm Password"}
                inputName="confirmPassword"
                inputType={"password"}
                value={values?.confirmPassword}
                onChange={handleChange}
                {...formValidate(errors, "confirmPassword")}
              />
            </div>
          </Fragment>
        )}

        <div className="relative col-span-4">
          <Select
            label={"Role"}
            placeholder="Select Role"
            selectedValue={values?.roleCode}
            dropdownData={[
              { name: "Applicant", value: "user" },
              { name: "Admin", value: "admin" },
            ]}
            setSelectedValue={(val) => {
              handleChange({
                target: { name: "roleCode", value: val },
              });
            }}
            {...formValidate(errors, "roleCode")}
          />
          <div className="w-full"></div>
        </div>
        {!data?._id && (
          <div className="relative col-span-4">
            <div className="text-xl font-bold">Temporary Password</div>
            <div className="flex items-center gap-3 mt-2">
              <div className="flex items-center gap-2 text-base font-medium">
                <Radio
                  isChecked={selectedOption === "autoPassword"}
                  value={"autoPassword"}
                  radioName={"radioValue"}
                  onChange={() => setSelectedOption("autoPassword")}
                  id="autoPassword"
                />
                 Auto-Generate Secure Password
              </div>
              <div className="flex items-center gap-2 text-base font-medium">
                <Radio
                  isChecked={selectedOption === "manualPassword"}
                  value={values?.radioValue}
                  radioName={"manualPassword"}
                  onChange={() => setSelectedOption("manualPassword")}
                  id="manualPassword"
                />
                 Manually Enter a New Password
              </div>
            </div>

            {values?.roleCode === "user" && (
              <Fragment>
                <div className="flex gap-2 items-center mt-5">
                  <Switch
                    checked={values?.forcePassword}
                    name="forcePassword"
                    onChange={handleChange}
                  />
                  Force Password Reset on First Login
                </div>
                <div className="text-base font-bold text-chestnut-rose underline mt-5">
                  Assign Permissions
                </div>
                <div className="flex gap-2 items-center mt-5 text-sm text-gable-green">
                  <Switch
                    className={
                      "!w-10 !h-5 before:!w-4 before:!h-4 peer-checked:before:!left-[22px]"
                    }
                    checked={values?.isActive}
                    name="isActive"
                    onChange={handleChange}
                  />
                  Activate Account Immediately
                </div>
                <div className="flex gap-2 items-center mt-5 text-sm text-gable-green">
                  <Switch
                    className={
                      "!w-10 !h-5 before:!w-4 before:!h-4 peer-checked:before:!left-[22px]"
                    }
                    checked={values?.welcomeEmail}
                    name="welcomeEmail"
                    onChange={handleChange}
                  />
                  Send Welcome Email with Login Details
                </div>
              </Fragment>
            )}
          </div>
        )}
      </div>
      <div className="relative col-span-4 mt-5">
        <div className="flex items-center justify-center gap-4">
          <Button
            buttonType="submit"
            buttonLabel={data?._id ? "Update User" : "Create User"}
            buttonClasses={
              "!rounded-full !px-8 !h-12 !border !border-chestnut-rose !bg-transparent !text-chestnut-rose hover:!bg-chestnut-rose hover:!text-white"
            }
            buttonLabelClasses={"mx-auto"}
            {...(isLoading && {
              loading: true,
              buttonIconPosition: "left",
              isDisable: true,
            })}
          />
          <Button
            buttonLabel={"Cancel"}
            buttonClasses={
              "!rounded-full !px-8 !h-12 !border !border-chestnut-rose !bg-transparent !text-chestnut-rose hover:!bg-chestnut-rose hover:!text-white"
            }
            buttonLabelClasses={"mx-auto"}
            buttonFunction={close}
          />
        </div>
      </div>
    </form>
  );
};

export default ManageUser;
